import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Link, Outlet } from "@tanstack/react-router";
import { type ReactNode } from "react";

import { AccountQueryProvider } from "../../account-query";
import { useTranslation } from "../../i18n";
import { useAdminAccess, useSession, useSignOut } from "../../route-shared";
import { EmptyState, LoadingBlock } from "../../ui";
import { buttonVariants } from "../ui/button";
import { TopLoader } from "../ui/top-loader";
import { useQueryErrorToast } from "../ui/use-query-error-toast";
import { UserMenu } from "../ui/user-menu";

/**
 * 管理后台的独立外壳：前台 topbar 不进入本空间，返回前台由顶栏显式承担。
 * 权限探测走 useAdminAccess（与前台头像菜单共享同一查询），安全边界仍在后端 API。
 */
export function AdminShell() {
  const { t } = useTranslation();
  const sessionQuery = useSession();
  const session = sessionQuery.data?.session ? sessionQuery.data : null;
  const adminQuery = useAdminAccess(session?.user?.id);
  const signOut = useSignOut();
  const userId = session?.user?.id;
  const accessError = !sessionQuery.isFetching && sessionQuery.error || !adminQuery.isFetching && adminQuery.error;
  useQueryErrorToast(accessError, t("workspace.loadingListError"), "admin-access-load");
  useQueryErrorToast(signOut.error, t("adminOperations.failed"), "admin-signout-failed");

  let body: ReactNode;
  if (signOut.isPending || sessionQuery.isLoading) {
    body = <LoadingBlock />;
  } else if (sessionQuery.isError && !session) {
    body = <div className="page" />;
  } else if (!session) {
    body = (
      <div className="page">
        <EmptyState title={t("admin.shell.signInRequiredTitle")} detail={t("admin.shell.signInRequiredDetail")} />
        <div className="center-actions">
          <Link className={buttonVariants({ variant: "default" })} to="/login">{t("nav.login")}</Link>
        </div>
      </div>
    );
  } else if (adminQuery.isLoading) {
    body = <LoadingBlock />;
  } else if (adminQuery.isError && adminQuery.data !== true) {
    body = <div className="page" />;
  } else if (adminQuery.data !== true) {
    body = (
      <div className="page">
        <EmptyState title={t("admin.shell.forbiddenTitle")} detail={t("admin.shell.forbiddenDetail")} />
        <div className="center-actions">
          <Link className={buttonVariants({ variant: "default" })} to="/">{t("admin.shell.backToSite")}</Link>
        </div>
      </div>
    );
  } else {
    body = (
      <AccountQueryProvider userId={userId}>
        <Outlet key={userId ?? "anonymous"} />
      </AccountQueryProvider>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-muted/30 text-foreground">
      <TopLoader />
      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background px-4 sm:px-6">
        <Link
          to="/"
          aria-label={t("admin.shell.backToSite")}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          <span className="hidden sm:inline">{t("admin.shell.backToSite")}</span>
        </Link>
        <div aria-hidden="true" className="h-4 w-px bg-border" />
        <div className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
          <ShieldCheck className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="truncate">{t("nav.admin")}</span>
        </div>
        <div className="ml-auto flex shrink-0 items-center">
          <UserMenu
            pending={sessionQuery.isLoading || signOut.isPending}
            isAdmin={!signOut.isPending && adminQuery.data === true}
            session={session}
            onSignOut={() => signOut.mutate()}
          />
        </div>
      </header>
      <main className="flex flex-1 flex-col">{body}</main>
    </div>
  );
}
